import { useCallback } from "react";

import {
  ENTRY_DATA_SECTION_KEYS as SECTION_KEYS,
  ENTRY_DATA_FINANCING_INFORMATION_GROUP_TABLE_KEY as FINANCING_INFORMATION,
} from "@frontend/common";

import {
  FinancingInformation,
  VentureCapitalFinancingInformationShellKeys,
} from "@/types/ventureCapital.types";
import {
  useTableCreatorFocusStatus,
  DEFAULT_FOCUS_STATUS,
} from "@FillInTableContainer/shared/context/TableCreatorFocusStatusContext";
import { useTableCreatorCurrentHistory } from "@FillInTableContainer/shared/context/TableCreatorHistoryContext";
import {
  useTableCreatorPositionInfo,
  DEFAULT_POSITION_INFO,
} from "@FillInTableContainer/shared/context/TableCreatorPositionInfoContext";
import { useEntryDataState } from "@stores/entryData";
import { getPressIdFromURL } from "@utils/getStateFromURL";

import type { PositionInfoProps } from "../VentureCapitalFinancingInformationCreatorShell";
import type { ArticleEntryData } from "@/types/articleEntryData.types";

interface UseHandleTableCreatorShellReturn {
  handleKeyDownToClearFinancingInformationShell: (event: KeyboardEvent) => void;
  handleClickToClearFinancingInformationShell: () => void;
}

export const useHandleVentureCapitalFinancingInformationCreatorShell = (
  positionPropsInfo: PositionInfoProps,
): UseHandleTableCreatorShellReturn => {
  const { changeCurrentHistory } = useTableCreatorCurrentHistory();
  const { info, changeInfo } = useTableCreatorPositionInfo();
  const { focusStatus, changeFocusStatus } = useTableCreatorFocusStatus();
  const [entryData, setEntryData] = useEntryDataState(getPressIdFromURL());

  const handleClearShell = useCallback(() => {
    const clearFinancingInformationShell = (): ArticleEntryData => {
      return {
        ...entryData,
        [SECTION_KEYS.ventureCapital]: {
          ...entryData[SECTION_KEYS.ventureCapital],
          [FINANCING_INFORMATION]: {
            ...entryData[SECTION_KEYS.ventureCapital]?.[FINANCING_INFORMATION],
            [info.shellKey as VentureCapitalFinancingInformationShellKeys]: {
              ...entryData[SECTION_KEYS.ventureCapital]?.[
                FINANCING_INFORMATION
              ]?.[info.shellKey as VentureCapitalFinancingInformationShellKeys],
              value: [],
            },
          } as FinancingInformation,
        },
      };
    };

    if (info.groupTableKey === FINANCING_INFORMATION) {
      setEntryData(clearFinancingInformationShell());
      changeCurrentHistory({
        entryData: clearFinancingInformationShell(),
        changedDataInfo: {
          target: "shell",
          action: "clear",
          position: {
            ...positionPropsInfo,
            value: null,
            valueIndex: null,
          },
        },
      });
    }

    changeFocusStatus(DEFAULT_FOCUS_STATUS);
    changeInfo(DEFAULT_POSITION_INFO);
  }, [
    changeCurrentHistory,
    changeFocusStatus,
    changeInfo,
    entryData,
    info,
    positionPropsInfo,
    setEntryData,
  ]);

  const handleClickToClearFinancingInformationShell = () => {
    if (focusStatus.type === "clear") {
      handleClearShell();
    }
  };

  const handleKeyDownToClearFinancingInformationShell = useCallback(
    (event: KeyboardEvent) => {
      if (focusStatus.type === "clear" && event.key === "Delete") {
        handleClearShell();
      }
    },
    [focusStatus.type, handleClearShell],
  );

  return {
    handleClickToClearFinancingInformationShell,
    handleKeyDownToClearFinancingInformationShell,
  };
};
